import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { getDocument } from '../pdfRuntime';

export default function InvoicePdfCanvas({ blob }) {
  const canvas = useRef(null);
  const [document, setDocument] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [error, setError] = useState('');
  const [rendering, setRendering] = useState(false);
  useEffect(() => {
    let alive = true, task;
    setDocument(null); setPageNumber(1); setError('');
    const reader = new FileReader();
    reader.onload = () => {
      if (!alive) return;
      task = getDocument({ data: new Uint8Array(reader.result), isEvalSupported: false });
      task.promise.then(pdf => { if (alive) setDocument(pdf); }).catch(() => { if (alive) setError('Unable to render this PDF. You can still download it.'); });
    };
    reader.onerror = () => { if (alive) setError('Unable to read the PDF preview.'); };
    reader.readAsArrayBuffer(blob);
    return () => { alive = false; if (reader.readyState === 1) reader.abort(); task?.destroy(); };
  }, [blob]);
  useEffect(() => {
    if (!document) return undefined;
    let alive = true, task;
    setRendering(true); setError('');
    document.getPage(pageNumber).then(page => {
      if (!alive) return;
      const initial = page.getViewport({ scale: 1 });
      const scale = Math.min(1.5, 1600 / initial.width, Math.sqrt(4000000 / (initial.width * initial.height)));
      const viewport = page.getViewport({ scale });
      canvas.current.width = Math.ceil(viewport.width); canvas.current.height = Math.ceil(viewport.height);
      task = page.render({ canvasContext: canvas.current.getContext('2d'), viewport });
      return task.promise;
    }).then(() => { if (alive) setRendering(false); }).catch(e => { if (alive && e.name !== 'RenderingCancelledException') { setError('Unable to render this page. You can still download the PDF.'); setRendering(false); } });
    return () => { alive = false; task?.cancel(); };
  }, [document, pageNumber]);
  return <div className="p-3">
    {error && <Alert variant="danger">{error}</Alert>}
    {!error && (!document || rendering) && <Spinner role="status" aria-label="Rendering invoice" animation="border" />}
    {document && <div className="d-flex justify-content-center align-items-center gap-2 mb-2">
      <Button size="sm" disabled={pageNumber <= 1 || rendering} onClick={() => setPageNumber(n => n - 1)}>Previous page</Button>
      <span>Page {pageNumber} of {document.numPages}</span>
      <Button size="sm" disabled={pageNumber >= document.numPages || rendering} onClick={() => setPageNumber(n => n + 1)}>Next page</Button>
    </div>}
    <canvas ref={canvas} role="img" aria-label={`Invoice PDF preview page ${pageNumber}`} style={{ width: '100%', height: 'auto', display: document && !error ? 'block' : 'none' }} />
  </div>;
}
